import { OnboardingRow } from "../../../types/onboarding";
import Observations from "./Observations";
import TraitsBadges from "./TraitsBadges";

const fmtMoney = (n: number | null | undefined) => {
  if (n === null || n === undefined) return "—";
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(n);
};

const fmtDate = (d: string | null) => {
  if (!d) return "—";
  const datePart = d.substring(0, 10);
  const [y, m, day] = datePart.split("-");
  if (!y || !m || !day) return d;
  return `${day}/${m}/${y}`;
};

const fmt = (v: unknown) =>
  v === null || v === undefined || v === "" ? "—" : String(v);

type Block = { title: string; fields: { label: string; value: React.ReactNode }[] };

const fmtGender = (g: string | null): string => {
  if (!g) return "—";
  const k = g.trim().toLowerCase();
  if (k === "male") return "Hombre";
  if (k === "female") return "Mujer";
  return g;
};

const fmtPrp = (
  prp: string | null,
  description: string | null,
): React.ReactNode => {
  if (!prp || prp.trim().toLowerCase() !== "yes") return "No";
  if (description && description.trim() !== "") {
    return (
      <span>
        Sí <span style={{ color: "#73879C" }}>· {description}</span>
      </span>
    );
  }
  return "Sí";
};

export default function ExpandedRow({ row }: { row: OnboardingRow }) {
  const blocks: Block[] = [
    {
      title: "Info cliente",
      fields: [
        { label: "Nombre completo", value: fmt(row.customer) },
        { label: "Fecha de nacimiento", value: fmtDate(row.birthdate) },
        { label: "DNI/NIE", value: fmt(row.dni) },
        { label: "Género", value: fmtGender(row.gender) },
        { label: "PRP", value: fmtPrp(row.prp, row.prpDescription) },
        { label: "Coberturas", value: <TraitsBadges traits={row.traits} /> },
      ],
    },
    {
      title: "Aseguradora",
      fields: [
        { label: "Carrier", value: fmt(row.carrier) },
        { label: "Medor", value: fmt(row.medor) },
      ],
    },
    {
      title: "Pricing",
      fields: [
        { label: "Prima anual", value: fmtMoney(row.annualPrice) },
        { label: "Frecuencia", value: fmt(row.frequency) },
        { label: "Tributos consorcio", value: fmtMoney(row.taxConsorcio) },
        { label: "Tributo fallecimiento", value: fmtMoney(row.taxFallecimiento) },
        { label: "Tributo IPA", value: fmtMoney(row.taxIpa) },
      ],
    },
    {
      title: "Fechas",
      fields: [
        { label: "Compra", value: fmtDate(row.purchaseDate) },
        { label: "Efecto", value: fmtDate(row.effectDate) },
        { label: "Firma", value: fmtDate(row.signatureDate) },
        { label: "Renovación", value: fmtDate(row.renewalDate) },
        { label: "Cancelación", value: fmtDate(row.cancellationDate) },
      ],
    },
    {
      title: "Pago",
      fields: [
        { label: "Método", value: fmt(row.paymentMethod) },
        { label: "Stripe ID", value: <Mono value={row.stripeCustomerId} /> },
        { label: "Last 4 tarjeta", value: fmt(row.last4Card) },
        { label: "Caducidad", value: fmt(row.expCard) },
      ],
    },
    {
      title: "Promoción",
      fields: [
        { label: "Promoción", value: fmt(row.promoName) },
        { label: "Promo ID", value: <Mono value={row.promoId} /> },
      ],
    },
    {
      title: "Cancelación",
      fields: [
        { label: "Razón", value: fmt(row.cancellationReason) },
        { label: "Tipo", value: fmt(row.cancellationType) },
        { label: "Solicitud", value: fmtDate(row.cancellationRequestDate) },
        { label: "Carrier", value: fmtDate(row.carrierCancellDate) },
        { label: "Póliza impagada previa", value: fmt(row.previousUnpaidPolicy) },
      ],
    },
    {
      title: "Tracking",
      fields: [
        { label: "Modo de venta", value: fmt(row.salesChannel) },
        { label: "Asesora", value: fmt(row.advisor) },
        { label: "Partner", value: fmt(row.partner) },
        { label: "Teléfono", value: fmt(row.memberPhone) },
        { label: "Ticket Hubspot", value: fmt(row.hubspotTicket) },
        {
          label: "Leads relacionados",
          value:
            row.relatedLeadIds.length > 1
              ? row.relatedLeadIds.join(", ")
              : "—",
        },
      ],
    },
  ];

  return (
    <div
      style={{
        padding: "1rem 1.5rem 1.25rem",
        background: "#f8f9fb",
        borderTop: "1px solid #e9ecef",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
          gap: "0.85rem",
        }}
      >
        {blocks.map((block) => (
          <section
            key={block.title}
            style={{
              background: "white",
              padding: "0.75rem 0.9rem",
              borderRadius: "0.55rem",
              borderLeft: "3px solid #0069d9",
              boxShadow: "0 1px 3px rgba(0,0,0,.04)",
            }}
          >
            <h4
              style={{
                margin: "0 0 0.55rem",
                fontSize: ".7rem",
                textTransform: "uppercase",
                fontWeight: 600,
                color: "#0069d9",
                letterSpacing: "0.5px",
              }}
            >
              {block.title}
            </h4>
            <dl style={{ margin: 0, display: "grid", gap: "0.3rem" }}>
              {block.fields.map((f) => (
                <div
                  key={f.label}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "120px 1fr",
                    fontSize: ".8rem",
                    gap: "0.5rem",
                  }}
                >
                  <dt style={{ color: "#73879C" }}>{f.label}</dt>
                  <dd
                    style={{
                      margin: 0,
                      color: "#1f2933",
                      wordBreak: "break-word",
                    }}
                  >
                    {f.value}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>

      <Observations leadUlid={row.leadUlid} />
    </div>
  );
}

function Mono({ value }: { value: string | null }) {
  if (!value) return <span>—</span>;
  return (
    <code style={{ fontSize: ".75rem", color: "#1f2933" }}>{value}</code>
  );
}
